import { GQL_QUERIES } from 'src/utils/analytics/newrelic/ErrorTracker';
import { graphQlRequest } from '../GraphQL/RequestHandler';

import {
  CHECK_REGISTRATION_QUERY,
  REGISTER_USER_TO_SEQUENTIAL_TRIVIA,
  SEQUENTIAL_TRIVIA_QUERY,
  SUBMIT_SEQUENTIAL_TRIVIA_ANSWER
} from '../GraphQL/Queries/TriviaQueries';
import { getLocalStorage } from '../../utils/browserStorageHandler';
import { USER_TYPE } from '../../utils/constants/localStorageConstants';

import { HTTP_HEADER_KEYS } from '../../utils/constants/HttpMethodsConstants';

export const GUEST_USER = ['guest', 'User'];

export enum AnswerState {
  UNASNWERED = '',
  ACTIVE = 'active',
  CORRECT = 'correct',
  INCORRECT = 'incorrect',
  SELECTED = 'selected'
}

export interface OptionDto {
  id: string;
  optionText: string;
  isCorrect: boolean;
}

export interface PollDto {
  id: string;
  pollType: string;
  displayTitle: string;
  options: OptionDto[];
  rewardId: string;
  expiresIn: number;
  startDate: string;
  endDate: string;
}

export interface PollResponseDto {
  polls: PollDto[];
}

export type Nullable<T> = T | null;
export interface OptionObject {
  id: string;
  optionText: string;
}

export interface UserAnswerResponseObject {
  userSelectedOptionId: Nullable<string>;
  questionId: string;
  correctAnswerId: string;
  isAnswerCorrect: boolean;
}

export interface QNAObject {
  id: string;
  gameId: string;
  questionText: string;
  backgroundImage: string;
  campaignId: string;
  options: OptionObject[];
  pointsForCorrectAnswer: number;
  userAnswerResponse: Nullable<UserAnswerResponseObject>;
}

export interface QNAState extends QNAObject {
  state: AnswerState;
  selected: Nullable<string>;
}

const getTriviaRequestHeaders = (): string[] => {
  const requestHeaders: string[] = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  if (getLocalStorage(USER_TYPE) === 'guest') {
    requestHeaders.push(HTTP_HEADER_KEYS.X_Z5_GUEST_TOKEN);
  } else {
    requestHeaders.push(HTTP_HEADER_KEYS.AUTHORIZATION);
  }
  return requestHeaders;
};

export const fetchTriviaQuestions = async (gameMapperId: string) => {
  const requestParams = {
    triviaCampaignId: gameMapperId
  };

  const requestHeaders: string[] = getTriviaRequestHeaders();
  try {
    const response = await graphQlRequest(
      SEQUENTIAL_TRIVIA_QUERY,
      requestParams,
      GQL_QUERIES.FETCH_SEQUENTIAL_TRIVIA_QUERY,
      requestHeaders
    );
    return response;
  } catch (error) {
    console.error('Error while fetching trivia questions: ', error);
    return null;
  }
};

export const submitQuizAnswer = async (props: {
  gameId: string;
  userSelectedOptionId: string;
  questionId: string;
}) => {
  const requestParams = {
    ...props
  };

  const requestHeaders: string[] = getTriviaRequestHeaders();
  try {
    const response = await graphQlRequest(
      SUBMIT_SEQUENTIAL_TRIVIA_ANSWER,
      requestParams,
      GQL_QUERIES.SUBMIT_SEQUENTIAL_TRIVIA_ANSWER,
      requestHeaders
    );
    return response;
  } catch (error) {
    console.error('Error while fetching trivia questions: ', error);
    return null;
  }
};

export const checkUserRegistration = async (props: { mapperId: string }) => {
  try {
    const requestParams = {
      ...props
    };
    const requestHeaders: string[] = getTriviaRequestHeaders();
    const response = await graphQlRequest(
      CHECK_REGISTRATION_QUERY,
      requestParams,
      GQL_QUERIES.CHECK_SEQUENTIAL_TRIVIA_REGISTRATION_QUERY,
      requestHeaders
    );
    return response;
  } catch (error) {
    console.error(error);
  }
  return {};
};

export const registerUserForTrivia = async (props: {
  userName: string;
  region: string;
  mapperId: string;
}) => {
  try {
    const requestParams = {
      ...props
    };
    const requestHeaders: string[] = getTriviaRequestHeaders();
    const res = await graphQlRequest(
      REGISTER_USER_TO_SEQUENTIAL_TRIVIA,
      requestParams,
      GQL_QUERIES.REGISTER_USER_TO_SEQUENTIAL_TRIVIA,
      requestHeaders
    );
    return res;
  } catch (error) {
    console.error(error);
  }
  return {};
};

export const getTriviaQNAQuestions = async (props: {
  triviaCampaignId: string;
}) => {
  const requestParams = {
    ...props
  };
  try {
    const res = await graphQlRequest(
      SEQUENTIAL_TRIVIA_QUERY,
      requestParams,
      GQL_QUERIES.FETCH_SEQUENTIAL_TRIVIA_QUERY
    );
    return res;
  } catch (error) {
    console.error(error);
  }
  return {};
};

export const getQNAList = (qna: QNAObject[]): QNAState[] =>
  qna?.map((item) => {
    const state = item.userAnswerResponse?.userSelectedOptionId
      ? item.userAnswerResponse.userSelectedOptionId ===
        item.userAnswerResponse.correctAnswerId
        ? AnswerState.CORRECT
        : AnswerState.INCORRECT
      : AnswerState.UNASNWERED;
    return {
      ...item,
      state,
      selected: item.userAnswerResponse?.userSelectedOptionId || null
    };
  });
